﻿/**
 * dgFav - Distribution Gauthier Favorites API v4.0
 * ═══════════════════════════════════════════════
 * Single source of truth for favorites + custom folders (multi-tag).
 * Works for guests (localStorage only) and logged-in users (+ Shopify App Proxy sync).
 *
 * NEW v4: Customer-defined folders. A favorite handle can belong to 0..N folders.
 *   Folders are pure tags ; the master `handles` list remains authoritative
 *   for "is this favorited?" — toggling the heart never affects folder content
 *   (except `remove()` which retires a handle from every folder for consistency).
 *
 * State shape (in-memory, localStorage, and Worker payload):
 *   {
 *     handles: string[],
 *     folders: [{ id: string, name: string, color: string, handles: string[] }]
 *   }
 *
 * Closed color palette:
 *   red, orange, yellow, green, teal, blue, purple, pink
 *
 * API:
 *   dgFav.list()                          -> string[]   master handle list
 *   dgFav.has(handle)                     -> boolean
 *   dgFav.toggle(handle)                  -> boolean    new wishlisted state
 *   dgFav.remove(handle)                  -> void       removes from master AND every folder
 *   dgFav.onChange(fn)                    -> void       fn(handles, folders)
 *   dgFav.syncFromServer()                -> Promise
 *   dgFav.syncToServer()                  -> Promise
 *
 *   dgFav.folders()                       -> Folder[]
 *   dgFav.foldersOf(handle)               -> string[]   folder ids containing this handle
 *   dgFav.createFolder(name, color?)      -> Folder
 *   dgFav.renameFolder(id, name)          -> boolean
 *   dgFav.setFolderColor(id, color)       -> boolean
 *   dgFav.deleteFolder(id)                -> boolean    folder gone, handles preserved
 *   dgFav.addToFolder(folderId, handle)   -> boolean
 *   dgFav.removeFromFolder(folderId, h)   -> boolean
 *   dgFav.colors()                        -> string[]   available palette
 *
 *   dgFav.isSyncEnabled()                 -> boolean
 *
 * Race-condition protection (carried over from v3.1, extended to all writes):
 *   `_lastWriteAt` is updated on every local mutation. If a server sync resolves
 *   AFTER a local write, the server response is discarded and the local state
 *   is pushed instead. Prevents "ghost" reverts when the user mutates while a
 *   GET is in flight.
 *
 * Config (injected by theme.liquid):
 *   window.DG_SYNC_CONFIG = {
 *     isLoggedIn: true, appProxyPath: "/apps/favoris"
 *   }
 */
(function () {
    'use strict';

    var STORAGE_KEY = 'dg:fav';
    var CONFIG = window.DG_SYNC_CONFIG || {};
    var APP_PROXY = (CONFIG.appProxyPath || '/apps/favoris').replace(/\/+$/, '');
    var IS_LOGGED_IN = !!(CONFIG.isLoggedIn && APP_PROXY);

    var ALLOWED_COLORS = ['red', 'orange', 'yellow', 'green', 'teal', 'blue', 'purple', 'pink'];
    var DEFAULT_COLOR = 'blue';

    var MAX_HANDLES = 500;
    var MAX_FOLDERS = 50;
    var MAX_FOLDER_NAME = 40;

    var _listeners = [];
    var _syncing = false;
    var _syncPromise = null;
    var _postQueue = Promise.resolve();
    var _lastWriteAt = 0;

    // ── State helpers ─────────────────────────────────────────────────────────

    function emptyState() {
        return { handles: [], folders: [] };
    }

    function isValidColor(c) {
        return typeof c === 'string' && ALLOWED_COLORS.indexOf(c) !== -1;
    }

    /** Coerce any input into a clean state object {handles, folders}. */
    function normalize(input) {
        if (!input) return emptyState();

        // Legacy: plain array of handles.
        if (Array.isArray(input)) {
            return { handles: cleanHandleList(input), folders: [] };
        }

        if (typeof input !== 'object') return emptyState();

        var handles = cleanHandleList(input.handles);
        var masterSet = {};
        for (var i = 0; i < handles.length; i++) masterSet[handles[i]] = true;

        var folders = [];
        var seenIds = {};
        var rawFolders = Array.isArray(input.folders) ? input.folders : [];
        for (var j = 0; j < rawFolders.length && folders.length < MAX_FOLDERS; j++) {
            var f = rawFolders[j];
            if (!f || typeof f !== 'object') continue;
            var id = typeof f.id === 'string' ? f.id.trim() : '';
            var name = typeof f.name === 'string' ? f.name.trim() : '';
            var color = isValidColor(f.color) ? f.color : DEFAULT_COLOR;
            if (!id || seenIds[id]) continue;
            if (!name) continue;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);

            var fhRaw = Array.isArray(f.handles) ? f.handles : [];
            var fhSeen = {};
            var fh = [];
            for (var k = 0; k < fhRaw.length; k++) {
                var h = fhRaw[k];
                if (typeof h !== 'string' || !h) continue;
                if (!masterSet[h]) continue; // must be subset of master
                if (fhSeen[h]) continue;
                fhSeen[h] = true;
                fh.push(h);
            }

            seenIds[id] = true;
            folders.push({ id: id, name: name, color: color, handles: fh });
        }

        return { handles: handles, folders: folders };
    }

    function cleanHandleList(arr) {
        if (!Array.isArray(arr)) return [];
        var out = [];
        var seen = {};
        for (var i = 0; i < arr.length && out.length < MAX_HANDLES; i++) {
            var h = arr[i];
            if (typeof h !== 'string' || !h) continue;
            if (h.length > 200) continue;
            if (seen[h]) continue;
            seen[h] = true;
            out.push(h);
        }
        return out;
    }

    function genFolderId() {
        var rnd = Math.random().toString(36).slice(2, 8);
        var stamp = Date.now().toString(36).slice(-4);
        return 'f_' + stamp + rnd;
    }

    // ── localStorage helpers ──────────────────────────────────────────────────

    function readLocal() {
        try {
            var raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) return emptyState();
            var parsed = JSON.parse(raw);
            return normalize(parsed);
        } catch (e) { return emptyState(); }
    }

    function writeLocal(state) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch (e) { /* quota exceeded or unavailable */ }
    }

    // ── Migration: copy old keys into new key (one-time) ──────────────────────

    function migrateOldKeys() {
        try {
            var current = readLocal();
            if (current.handles.length > 0 || current.folders.length > 0) return;

            var legacyKeys = ['dg-favorites', 'dgFavorites', 'dg_favorites'];
            for (var i = 0; i < legacyKeys.length; i++) {
                var raw = localStorage.getItem(legacyKeys[i]);
                if (!raw) continue;
                try {
                    var parsed = JSON.parse(raw);
                    var migrated = normalize(parsed);
                    if (migrated.handles.length > 0) {
                        writeLocal(migrated);
                        console.debug('[dgFav] Migrated', migrated.handles.length, 'favorites from', legacyKeys[i]);
                        break;
                    }
                } catch (e) { /* ignore parse error */ }
            }
        } catch (e) { /* ignore migration errors */ }
    }

    // ── Notify listeners ──────────────────────────────────────────────────────

    function notifyChange() {
        var state = readLocal();
        for (var i = 0; i < _listeners.length; i++) {
            try { _listeners[i](state.handles, state.folders); } catch (e) { /* listener error */ }
        }
    }

    // ── Worker communication ──────────────────────────────────────────────────

    function workerFetch(method, body) {
        if (!IS_LOGGED_IN) return Promise.resolve(null);

        var opts = {
            method: method,
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json'
            }
        };

        var url = APP_PROXY;
        if (method !== 'GET') {
            opts.body = JSON.stringify(body);
        }

        return fetch(url, opts)
            .then(function (res) {
                if (!res.ok) {
                    console.warn('[dgFav] Worker HTTP', res.status);
                    return null;
                }
                return res.json();
            })
            .catch(function (err) {
                console.warn('[dgFav] Worker error:', err.message);
                return null;
            });
    }

    function enqueueServerWrite() {
        _postQueue = _postQueue
            .catch(function () {
                // Keep the queue alive after a failed write.
            })
            .then(function () {
                var s = readLocal();
                return workerFetch('POST', {
                    handles: s.handles,
                    folders: s.folders
                }).then(function () {
                    console.debug('[dgFav] Synced to server:', s.handles.length, 'fav,', s.folders.length, 'folders');
                });
            });
        return _postQueue;
    }

    /**
     * Push the latest local state to the server. Waits for any in-flight
     * GET sync to settle first, then pushes the freshest local snapshot.
     */
    function pushLocalToServer() {
        if (!IS_LOGGED_IN) return Promise.resolve();
        var doSync = enqueueServerWrite;
        if (_syncPromise) {
            return _syncPromise.then(doSync);
        }
        return doSync();
    }

    /**
     * Mutate local state via mutator(state) -> mutated state, then write,
     * mark _lastWriteAt, notify listeners, push to server.
     */
    function mutate(mutator) {
        var s = readLocal();
        var next = mutator(s);
        if (!next) return false;
        writeLocal(next);
        _lastWriteAt = Date.now();
        notifyChange();
        pushLocalToServer();
        return true;
    }

    // ══════════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ══════════════════════════════════════════════════════════════════════════

    window.dgFav = {

        list: function () {
            return readLocal().handles;
        },

        has: function (handle) {
            if (!handle) return false;
            return readLocal().handles.indexOf(handle) !== -1;
        },

        toggle: function (handle) {
            if (!handle) return false;
            var state = readLocal();
            var idx = state.handles.indexOf(handle);
            var nowWishlisted;
            if (idx === -1) {
                if (state.handles.length >= MAX_HANDLES) {
                    console.warn('[dgFav] Max favorites reached:', MAX_HANDLES);
                    return false;
                }
                state.handles.push(handle);
                nowWishlisted = true;
            } else {
                state.handles.splice(idx, 1);
                // Cascade: remove from every folder.
                for (var i = 0; i < state.folders.length; i++) {
                    var fh = state.folders[i].handles;
                    var fIdx = fh.indexOf(handle);
                    if (fIdx !== -1) fh.splice(fIdx, 1);
                }
                nowWishlisted = false;
            }
            writeLocal(state);
            _lastWriteAt = Date.now();
            notifyChange();
            pushLocalToServer();
            return nowWishlisted;
        },

        remove: function (handle) {
            if (!handle) return;
            mutate(function (state) {
                var idx = state.handles.indexOf(handle);
                if (idx === -1) return null;
                state.handles.splice(idx, 1);
                for (var i = 0; i < state.folders.length; i++) {
                    var fh = state.folders[i].handles;
                    var fIdx = fh.indexOf(handle);
                    if (fIdx !== -1) fh.splice(fIdx, 1);
                }
                return state;
            });
        },

        onChange: function (fn) {
            if (typeof fn === 'function') _listeners.push(fn);
        },

        // ── Folders ───────────────────────────────────────────────────────────

        folders: function () {
            return readLocal().folders.map(function (f) {
                return { id: f.id, name: f.name, color: f.color, handles: f.handles.slice() };
            });
        },

        foldersOf: function (handle) {
            if (!handle) return [];
            var state = readLocal();
            var ids = [];
            for (var i = 0; i < state.folders.length; i++) {
                if (state.folders[i].handles.indexOf(handle) !== -1) {
                    ids.push(state.folders[i].id);
                }
            }
            return ids;
        },

        colors: function () {
            return ALLOWED_COLORS.slice();
        },

        createFolder: function (name, color) {
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return null;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            color = isValidColor(color) ? color : DEFAULT_COLOR;

            var state = readLocal();
            if (state.folders.length >= MAX_FOLDERS) {
                console.warn('[dgFav] Max folders reached:', MAX_FOLDERS);
                return null;
            }
            var id = genFolderId();
            var folder = { id: id, name: name, color: color, handles: [] };
            state.folders.push(folder);
            writeLocal(state);
            _lastWriteAt = Date.now();
            notifyChange();
            pushLocalToServer();
            return { id: folder.id, name: folder.name, color: folder.color, handles: [] };
        },

        renameFolder: function (id, name) {
            if (!id) return false;
            name = (typeof name === 'string' ? name : '').trim();
            if (!name) return false;
            if (name.length > MAX_FOLDER_NAME) name = name.slice(0, MAX_FOLDER_NAME);
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].name = name;
                        return state;
                    }
                }
                return null;
            });
        },

        setFolderColor: function (id, color) {
            if (!id || !isValidColor(color)) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders[i].color = color;
                        return state;
                    }
                }
                return null;
            });
        },

        deleteFolder: function (id) {
            if (!id) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === id) {
                        state.folders.splice(i, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        addToFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                if (state.handles.indexOf(handle) === -1) return null; // not a favorite
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        if (state.folders[i].handles.indexOf(handle) !== -1) return null; // already in
                        state.folders[i].handles.push(handle);
                        return state;
                    }
                }
                return null;
            });
        },

        removeFromFolder: function (folderId, handle) {
            if (!folderId || !handle) return false;
            return mutate(function (state) {
                for (var i = 0; i < state.folders.length; i++) {
                    if (state.folders[i].id === folderId) {
                        var idx = state.folders[i].handles.indexOf(handle);
                        if (idx === -1) return null;
                        state.folders[i].handles.splice(idx, 1);
                        return state;
                    }
                }
                return null;
            });
        },

        // ── Sync ──────────────────────────────────────────────────────────────

        syncFromServer: function () {
            if (!IS_LOGGED_IN) return Promise.resolve();
            if (_syncPromise) return _syncPromise;

            _syncing = true;
            var syncStartTime = Date.now();

            _syncPromise = workerFetch('GET')
                .then(function (data) {
                    // If a local write happened during the GET, prefer local state.
                    if (_lastWriteAt > syncStartTime) {
                        console.debug('[dgFav] Local write during sync — pushing local state');
                        var localCurrent = readLocal();
                        return enqueueServerWrite();
                    }

                    if (!data) {
                        // No server response at all (HTTP error) — keep local untouched.
                        return;
                    }

                    var server = normalize({
                        handles: data.handles,
                        folders: data.folders
                    });

                    // If server is empty but we have something locally, push it.
                    if (server.handles.length === 0 && server.folders.length === 0) {
                        var localOnly = readLocal();
                        if (localOnly.handles.length > 0 || localOnly.folders.length > 0) {
                            console.debug('[dgFav] Pushing local-only state to server');
                            return enqueueServerWrite();
                        }
                        return;
                    }

                    var local = readLocal();
                    writeLocal(server);

                    var changed =
                        server.handles.length !== local.handles.length
                        || server.folders.length !== local.folders.length
                        || server.handles.some(function (h) { return local.handles.indexOf(h) === -1; })
                        || JSON.stringify(server.folders) !== JSON.stringify(local.folders);

                    if (changed) {
                        console.debug('[dgFav] Synced from server:',
                            server.handles.length, 'fav,',
                            server.folders.length, 'folders');
                        notifyChange();
                    } else {
                        console.debug('[dgFav] Synced (no change)');
                    }
                })
                .catch(function (err) {
                    console.warn('[dgFav] Sync error:', err.message || err);
                })
                .then(function () {
                    _syncing = false;
                    _syncPromise = null;
                });

            return _syncPromise;
        },

        syncToServer: function () {
            if (!IS_LOGGED_IN) return Promise.resolve();
            var s = readLocal();
            return enqueueServerWrite().then(function () {
                console.debug('[dgFav] Pushed', s.handles.length, 'fav /', s.folders.length, 'folders');
            });
        },

        isSyncEnabled: function () {
            return IS_LOGGED_IN;
        }
    };

    // ── Auto-init ─────────────────────────────────────────────────────────────

    migrateOldKeys();

    if (IS_LOGGED_IN) {
        window.dgFav.syncFromServer();
        console.debug('[dgFav] Init: logged in - app proxy sync started');
    } else {
        console.debug('[dgFav] Init: guest mode (localStorage only)');
    }

})();
